import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSearch } from '@fortawesome/free-solid-svg-icons';
import { categories } from '../constants/floraFaunaData';
import { API_BASE_URL } from '../config';

const CategoryDetails = () => {
    const { categoryId } = useParams();
    const category = categories.find(c => c.id === categoryId);
    const [speciesList, setSpeciesList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSpecies = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/families/read.php`);
                const data = await response.json();
                if (response.ok && data.records) {
                    const filtered = data.records.filter(f => f.category_id === categoryId);
                    setSpeciesList(filtered);
                } else {
                    setSpeciesList([]);
                }
            } catch (error) {
                console.error('Error fetching species:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSpecies();
    }, [categoryId]);

    if (!category) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-500">
                <FontAwesomeIcon icon={faLeaf} className="text-4xl mb-4 text-emerald-100" />
                <p>Category not found</p>
                <Link to="/flora-fauna" className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium">Return Home</Link>
            </div>
        );
    }

    return (
        <section className="min-h-screen bg-white font-sans selection:bg-emerald-100 selection:text-emerald-900">
            {/* Minimal Header */}
            {/* <div className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
                <div className="container mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/flora-fauna" className="group flex items-center gap-3 text-slate-500 hover:text-emerald-800 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-emerald-50 transition-colors">
                            <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-xs">Back</span>
                    </Link>
                    <div className="text-slate-300">
                        <FontAwesomeIcon icon={category.icon} />
                    </div>
                </div>
            </div> */}

            <div className="max-w-7xl mx-auto px-6 pt-32 pb-24">
                {/* Hero Title */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-20 max-w-4xl"
                >
                    <span className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-xs mb-4 block pl-1">
                        Flora & Fauna Collection
                    </span>
                    <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold text-slate-900 mb-6 tracking-tight leading-none font-display">
                        {category.label}
                        <span className="block text-xl sm:text-3xl md:text-4xl font-sans font-medium text-slate-400 mt-2 italic tracking-normal">
                            {category.subLabel}
                        </span>
                    </h1>
                    <div className="h-1 w-24 bg-emerald-500 rounded-full mt-8"></div>
                </motion.div>

                {/* Filter / Stats Bar (Visual only for now) */}
                <div className="flex flex-wrap items-center justify-between border-y border-slate-100 py-6 mb-16 gap-6">
                    <div className="flex items-center gap-8 text-sm text-slate-500 font-sans">
                        <div className="flex flex-col">
                            <span className="font-black text-slate-900 text-lg leading-none">{category.stats.sl}</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-2">Recorded in SL</span>
                        </div>
                        <div className="w-px h-8 bg-slate-100"></div>
                        <div className="flex flex-col">
                            <span className="font-black text-emerald-600 text-lg leading-none">{category.stats.endemic}</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400 mt-2">Endemic</span>
                        </div>
                    </div>

                    <div className="relative group">
                        <input
                            type="text"
                            placeholder="Search species..."
                            className="bg-slate-50 border-none rounded-full py-3 pl-12 pr-6 text-sm w-64 focus:ring-1 focus:ring-emerald-500 transition-all placeholder:text-slate-400 text-slate-700"
                        />
                        <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-16">
                    {loading ? (
                        <div className="col-span-full py-20 flex justify-center">
                            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
                        </div>
                    ) : speciesList.length > 0 ? (
                        speciesList.map((family, i) => (
                            <Link to={`/flora-fauna/family/${family.id}`} key={family.id} className="group block">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="relative"
                                >
                                    {/* Image Container */}
                                    <div className="aspect-[4/5] overflow-hidden bg-slate-100 mb-6 relative">
                                        <div className="absolute inset-0 bg-emerald-900/0 group-hover:bg-emerald-900/10 transition-colors z-10 duration-500"></div>
                                        {family.image_url ? (
                                            <img
                                                src={family.image_url}
                                                alt={family.name}
                                                className="w-full h-full object-cover transform scale-100 group-hover:scale-110 transition-transform duration-700 ease-out grayscale-[0.2] group-hover:grayscale-0"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <FontAwesomeIcon icon={faLeaf} className="text-4xl" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="pr-4">
                                        <p className="text-emerald-700 text-[10px] font-black uppercase tracking-widest mb-2 opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 font-sans">
                                            Family
                                        </p>
                                        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 group-hover:text-emerald-800 transition-colors leading-tight font-display">
                                            {family.name}
                                        </h3>
                                        <p className="text-sm font-sans italic text-slate-400 group-hover:text-slate-500 transition-colors">
                                            {family.common_name}
                                        </p>
                                    </div>

                                    {/* Hover Indicator */}
                                    <div className="absolute -bottom-4 left-0 w-0 h-0.5 bg-emerald-500 group-hover:w-12 transition-all duration-500 ease-out"></div>
                                </motion.div>
                            </Link>
                        ))
                    ) : (
                        <div className="col-span-full py-32 text-center">
                            <div className="w-24 h-24 mx-auto bg-slate-50 rounded-full flex items-center justify-center mb-6">
                                <FontAwesomeIcon icon={faLeaf} className="text-slate-200 text-3xl" />
                            </div>
                            <h3 className="text-xl font-serif text-slate-900 mb-2">Collection Coming Soon</h3>
                            <p className="text-slate-400 max-w-md mx-auto">
                                We are currently curating the {category.label.toLowerCase()} collection. Please check back later for updates.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default CategoryDetails;
